import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  GitCompareArrows,
  ChevronDown,
  FileSearch,
  FileText,
  Globe,
  RefreshCw
} from 'lucide-react';
import ExpandableDescription from './ExpandableDescription';
import { buildDocumentSharePath } from '../utils/documentLinks';

const MOBILE_BREAKPOINT = 1024;
const MOBILE_SHEET_PEEK = 86;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

function getPortalShortLabel(label = '') {
  return label.replace(/\s*\(.+\)\s*$/, '').trim() || label;
}

function getPortalSourceLabel(label = '') {
  const sourceMatch = label.match(/\(([^)]+)\)/);
  return sourceMatch ? sourceMatch[1].replace(/^Documentos\s*-\s*/i, '') : 'Portal fiscal';
}

function applyDragCurve(distance, totalDistance) {
  const safeTotal = Math.max(totalDistance, 1);
  const normalized = clamp(distance / safeTotal, 0, 1);
  const curved = (Math.exp(normalized * 1.75) - 1) / (Math.exp(1.75) - 1);
  return curved * safeTotal;
}

function ResultsContent({
  comparableSeriesMap,
  documentPortalName,
  emptyActionLabel,
  emptyControls,
  expandedCategories,
  filteredResults,
  groupedResults,
  isFetching,
  onCompareDocument,
  onEmptyAction,
  onToggleCategory,
  searchTerm
}) {
  if (isFetching) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((index) => (
          <div key={index} className="border border-gray-100 dark:border-gray-700/50 rounded-2xl overflow-hidden bg-gray-50/50 dark:bg-gray-800/30 animate-pulse">
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="h-6 w-6 rounded-lg bg-gray-200 dark:bg-gray-700"></div>
                <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
              </div>
              <div className="h-4 w-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (filteredResults.length === 0) {
    return (
      <div className="flex h-full min-h-[18rem] flex-col items-center justify-center gap-4 py-10 text-center">
        <img
          src="/mascot-ops.png"
          alt="Mascote Ops"
          className="h-28 w-28 object-contain"
        />
        <div className="max-w-sm">
          <p className="text-sm font-black uppercase tracking-widest text-gray-700 dark:text-gray-200">
            {searchTerm ? 'Nenhum resultado para a busca' : 'Nenhum documento listado'}
          </p>
          {searchTerm ? (
            <p className="mt-2 text-xs leading-5 text-gray-500 dark:text-gray-400">
              O mascote não encontrou nada com esse filtro. Você pode limpar a busca e tentar novamente.
            </p>
          ) : (
            <p className="mt-2 text-xs leading-5 text-gray-500 dark:text-gray-400">
              Selecione o DF-e abaixo para continuar.
            </p>
          )}
        </div>
        {!searchTerm && emptyControls ? (
          <div className="w-full max-w-2xl">
            {emptyControls}
          </div>
        ) : null}
        {onEmptyAction && (searchTerm || !emptyControls) ? (
          <button
            type="button"
            onClick={onEmptyAction}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-teal-600 px-4 text-[11px] font-black uppercase tracking-widest text-white transition-colors hover:bg-teal-700"
          >
            <FileSearch className="h-4 w-4" />
            {emptyActionLabel}
          </button>
        ) : null}
        <span className="sr-only">
          {searchTerm ? 'Nenhum resultado para a busca' : 'Nenhum documento listado'}
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {Object.entries(groupedResults).map(([category, items]) => (
        <div key={category} className="border border-gray-100 dark:border-gray-700 rounded-2xl overflow-hidden bg-white dark:bg-gray-800/50">
          <button
            onClick={() => onToggleCategory(category)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="h-6 w-6 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-[10px] font-black text-gray-500">
                {items.length}
              </div>
              <span className="text-sm font-black uppercase tracking-widest text-gray-700 dark:text-gray-300">{category}</span>
            </div>
            <ChevronDown size={16} className={`text-gray-400 transition-transform ${expandedCategories[category] ? 'rotate-180' : ''}`} />
          </button>

          {expandedCategories[category] ? (
            <div className="px-4 pb-4">
              <div className="space-y-2">
                {items.map((item, index) => (
                  <div key={`${item.url}-${index}`} className={`flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 rounded-xl bg-gray-50 dark:bg-gray-900 border border-transparent hover:border-teal-200 dark:hover:border-teal-800 transition-all gap-3 group ${item.discontinued ? 'opacity-60 grayscale-[0.5]' : ''}`}>
                    <div className="flex flex-col min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-bold text-gray-700 dark:text-gray-200 break-words sm:truncate">{item.nome}</span>
                        {item.discontinued && (
                          <span className="inline-flex items-center rounded-md bg-red-50 px-1.5 py-0.5 text-[9px] font-bold text-red-700 ring-1 ring-inset ring-red-600/20 dark:bg-red-900/20 dark:text-red-400 dark:ring-red-900/50 uppercase tracking-widest shrink-0">
                            Descontinuado
                          </span>
                        )}
                        {item.isLatest && (
                          <span className="inline-flex items-center rounded-md bg-blue-50 px-1.5 py-0.5 text-[9px] font-bold text-blue-700 ring-1 ring-inset ring-blue-600/20 dark:bg-blue-900/20 dark:text-blue-400 dark:ring-blue-900/50 uppercase tracking-widest shrink-0">
                            Documento Mais Recente
                          </span>
                        )}
                      </div>
                      {item.description && (
                        <ExpandableDescription
                          text={item.description}
                          className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5"
                          buttonClassName="text-teal-600 dark:text-teal-400"
                        />
                      )}
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">{item.data || 'Sem data'}</span>
                      </div>
                    </div>
                    <div className="flex w-full sm:w-auto items-center gap-2 shrink-0">
                      {comparableSeriesMap[item.url] ? (
                        <button
                          type="button"
                          onClick={() => onCompareDocument(item)}
                          className="h-10 flex-1 sm:flex-none sm:h-8 sm:w-8 flex items-center justify-center rounded-lg bg-white dark:bg-gray-800 text-sky-600 border border-gray-200 dark:border-gray-700 hover:bg-sky-600 hover:text-white transition-colors"
                          title={`Comparar série ${comparableSeriesMap[item.url]}`}
                        >
                          <GitCompareArrows size={14} />
                        </button>
                      ) : null}

                      <a
                        href={buildDocumentSharePath(item, { portalName: documentPortalName })}
                        className="h-10 flex-1 sm:flex-none sm:h-8 sm:w-8 flex items-center justify-center rounded-lg bg-white dark:bg-gray-800 text-teal-600 border border-gray-200 dark:border-gray-700 hover:bg-teal-600 hover:text-white transition-colors"
                      >
                        <FileText size={14} />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function HeaderOnlineFallbackNotice({ isFetching, onSearchOnlineFallback, compact = false }) {
  return (
    <div className={`flex min-w-0 items-center gap-3 rounded-2xl border border-cyan-200 bg-cyan-50/80 px-3 py-2 dark:border-cyan-900/50 dark:bg-cyan-950/30 ${compact ? 'w-full' : 'max-w-xl'}`}>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-black uppercase tracking-wider text-cyan-800 dark:text-cyan-200">
          Busca online disponível
        </p>
        <p className="truncate text-xs text-cyan-700/80 dark:text-cyan-200/75">
          Não encontrou? Consulte o portal para buscar publicações fora da base.
        </p>
      </div>
      <button
        type="button"
        onClick={onSearchOnlineFallback}
        disabled={isFetching}
        className="inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-xl bg-teal-600 px-3 text-[10px] font-black uppercase tracking-widest text-white transition-colors hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Globe className="h-4 w-4" />
        Consultar
      </button>
    </div>
  );
}

export default function ResultsPanel({
  comparableSeriesMap = {},
  comparatorBadgeCount = 0,
  comparatorContent = null,
  expandedCategories,
  filteredResults,
  groupedResults,
  isFetching,
  isComparatorOpen = false,
  lastExecutionAt,
  onCompareDocument,
  onFilterChange,
  onFetchDocuments,
  onSearchOnlineFallback,
  onToggleCategory,
  portals = {},
  selectedPortal = '',
  onPortalChange,
  searchTerm,
  showOnlineFallbackPrompt = false
}) {
  const [isMobileViewport, setIsMobileViewport] = useState(() => window.innerWidth < MOBILE_BREAKPOINT);
  const [viewportHeight, setViewportHeight] = useState(() => window.innerHeight);
  const [isMobileSheetOpen, setIsMobileSheetOpen] = useState(false);
  const [dragTranslate, setDragTranslate] = useState(null);
  const dragStartYRef = useRef(0);
  const documentPortalName = portals[selectedPortal]?.label || '';
  const dragStartOpenRef = useRef(false);
  const draggingRef = useRef(false);
  const dragMovedRef = useRef(false);
  const dragTranslateRef = useRef(null);
  const lockedScrollYRef = useRef(0);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileViewport(window.innerWidth < MOBILE_BREAKPOINT);
      setViewportHeight(window.innerHeight);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isMobileViewport) {
      setIsMobileSheetOpen(false);
      setDragTranslate(null);
    }
  }, [isMobileViewport]);

  useEffect(() => {
    if (!isMobileViewport || !isComparatorOpen) return;

    setIsMobileSheetOpen(true);
    dragTranslateRef.current = null;
    setDragTranslate(null);
  }, [isComparatorOpen, isMobileViewport]);

  useEffect(() => {
    const shouldLockPage = isMobileViewport && (isMobileSheetOpen || dragTranslate !== null);
    if (!shouldLockPage) return undefined;

    const htmlStyle = document.documentElement.style;
    const bodyStyle = document.body.style;
    const previousHtmlOverflow = htmlStyle.overflow;
    const previousHtmlOverscroll = htmlStyle.overscrollBehaviorY;
    const previousBodyOverflow = bodyStyle.overflow;
    const previousBodyOverscroll = bodyStyle.overscrollBehaviorY;
    const previousBodyPosition = bodyStyle.position;
    const previousBodyTop = bodyStyle.top;
    const previousBodyWidth = bodyStyle.width;
    const previousBodyLeft = bodyStyle.left;
    const previousBodyRight = bodyStyle.right;

    lockedScrollYRef.current = window.scrollY;

    htmlStyle.overflow = 'hidden';
    htmlStyle.overscrollBehaviorY = 'none';
    bodyStyle.overflow = 'hidden';
    bodyStyle.overscrollBehaviorY = 'none';
    bodyStyle.position = 'fixed';
    bodyStyle.top = `-${lockedScrollYRef.current}px`;
    bodyStyle.left = '0';
    bodyStyle.right = '0';
    bodyStyle.width = '100%';

    return () => {
      htmlStyle.overflow = previousHtmlOverflow;
      htmlStyle.overscrollBehaviorY = previousHtmlOverscroll;
      bodyStyle.overflow = previousBodyOverflow;
      bodyStyle.overscrollBehaviorY = previousBodyOverscroll;
      bodyStyle.position = previousBodyPosition;
      bodyStyle.top = previousBodyTop;
      bodyStyle.width = previousBodyWidth;
      bodyStyle.left = previousBodyLeft;
      bodyStyle.right = previousBodyRight;
      window.scrollTo(0, lockedScrollYRef.current);
    };
  }, [dragTranslate, isMobileSheetOpen, isMobileViewport]);

  const mobileExpandedHeight = useMemo(() => {
    const safeViewportHeight = viewportHeight || 0;
    return Math.max(safeViewportHeight, 320);
  }, [viewportHeight]);

  const mobileCollapsedOffset = Math.max(mobileExpandedHeight - MOBILE_SHEET_PEEK, 0);

  const currentMobileTranslate = dragTranslate ?? (isMobileSheetOpen ? 0 : mobileCollapsedOffset);
  const mobileOpenProgress = mobileCollapsedOffset === 0
    ? 1
    : clamp(1 - (currentMobileTranslate / mobileCollapsedOffset), 0, 1);
  const showCloseHint = mobileOpenProgress > 0.42;
  const mobileCornerRadius = Math.round(28 * Math.pow(1 - mobileOpenProgress, 1.15));

  const resolveTranslateFromDelta = (deltaY, startedOpen) => {
    if (startedOpen) {
      const draggedDistance = applyDragCurve(Math.max(deltaY, 0), mobileCollapsedOffset);
      return clamp(draggedDistance, 0, mobileCollapsedOffset);
    }

    const upwardDistance = applyDragCurve(Math.max(-deltaY, 0), mobileCollapsedOffset);
    return clamp(mobileCollapsedOffset - upwardDistance, 0, mobileCollapsedOffset);
  };

  const beginDrag = (event) => {
    if (!isMobileViewport) return;

    draggingRef.current = true;
    dragMovedRef.current = false;
    dragStartYRef.current = event.clientY;
    dragStartOpenRef.current = isMobileSheetOpen;
    dragTranslateRef.current = isMobileSheetOpen ? 0 : mobileCollapsedOffset;
    setDragTranslate(dragTranslateRef.current);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleDrag = (event) => {
    if (!draggingRef.current || !isMobileViewport) return;

    const deltaY = event.clientY - dragStartYRef.current;
    if (Math.abs(deltaY) > 6) {
      dragMovedRef.current = true;
    }
    const nextTranslate = resolveTranslateFromDelta(deltaY, dragStartOpenRef.current);
    dragTranslateRef.current = nextTranslate;
    setDragTranslate(nextTranslate);
  };

  const endDrag = () => {
    if (!draggingRef.current || !isMobileViewport) return;

    draggingRef.current = false;
    const finalTranslate = dragTranslateRef.current ?? dragTranslate ?? (isMobileSheetOpen ? 0 : mobileCollapsedOffset);
    const shouldOpen = finalTranslate < mobileCollapsedOffset * 0.58;
    setIsMobileSheetOpen(shouldOpen);
    dragTranslateRef.current = null;
    setDragTranslate(null);
  };

  const handleSheetToggle = () => {
    if (dragMovedRef.current) {
      dragMovedRef.current = false;
      return;
    }

    setIsMobileSheetOpen((previous) => !previous);
  };

  const emptyActionLabel = searchTerm
    ? 'Limpar filtro'
    : showOnlineFallbackPrompt
      ? 'Consultar online'
      : 'Consultar documentos';
  const portalOptions = useMemo(
    () => Object.values(portals).map((portal) => ({
      ...portal,
      value: portal.id,
      shortLabel: getPortalShortLabel(portal.label),
      sourceLabel: getPortalSourceLabel(portal.label)
    })),
    [portals]
  );
  const isEmbeddedComparatorView = Boolean(isComparatorOpen && comparatorContent);
  const mobileSheetTitle = isEmbeddedComparatorView ? 'Comparador de documentos' : 'Documentos Encontrados';
  const mobileSheetBadgeValue = isEmbeddedComparatorView ? comparatorBadgeCount : filteredResults.length;
  const mobileSheetHint = isEmbeddedComparatorView
    ? (showCloseHint ? 'Arraste para fechar' : 'Toque para continuar a comparação')
    : (showCloseHint ? 'Arraste para fechar' : 'Arraste para cima para expandir');

  const handleEmptyAction = () => {
    if (searchTerm) {
      onFilterChange('');
      return;
    }

    if (showOnlineFallbackPrompt && onSearchOnlineFallback) {
      onSearchOnlineFallback();
      return;
    }

    onFetchDocuments?.();
  };

  const emptyControls = !searchTerm ? (
    <div className="w-full space-y-4 text-left">
      <div
        role="radiogroup"
        aria-label="Portais do Documento Fiscal"
        className="grid max-h-56 grid-cols-2 gap-2 overflow-y-auto pr-1 sm:max-h-none sm:grid-cols-3"
      >
        {portalOptions.map((portal) => {
          const isSelected = portal.value === selectedPortal;

          return (
            <button
              key={portal.value}
              type="button"
              role="radio"
              aria-checked={isSelected}
              onClick={() => onPortalChange?.(portal.value)}
              className={`flex min-h-[4.25rem] flex-col justify-between rounded-lg border px-3 py-2 text-left transition-all active:scale-[0.98] ${isSelected
                  ? 'border-teal-400 bg-teal-50 text-teal-800 shadow-sm dark:border-teal-500/60 dark:bg-teal-900/20 dark:text-teal-100'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-teal-200 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-teal-700/70 dark:hover:bg-gray-700/60'
                }`}
            >
              <div className="flex items-start justify-between gap-2">
                <span className="break-words text-xs font-black uppercase tracking-widest">
                  {portal.shortLabel}
                </span>
                <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${isSelected
                    ? 'border-teal-500 bg-teal-500 text-white'
                    : 'border-gray-300 bg-white text-transparent dark:border-gray-600 dark:bg-gray-900'
                  }`}>
                  <Check className="h-3.5 w-3.5" />
                </span>
              </div>
              <span className={`mt-2 text-[10px] font-bold uppercase tracking-wider ${isSelected
                  ? 'text-teal-700 dark:text-teal-200'
                  : 'text-gray-400 dark:text-gray-500'
                }`}>
                {portal.sourceLabel}
              </span>
            </button>
          );
        })}
      </div>
      <button
        type="button"
        onClick={handleEmptyAction}
        disabled={isFetching || !selectedPortal}
        className={`flex h-11 w-full items-center justify-center gap-2 rounded-xl px-6 text-xs font-black uppercase tracking-wider text-white transition-all active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 ${portals[selectedPortal]?.hasDatabase && !showOnlineFallbackPrompt
            ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-md shadow-blue-500/20'
            : 'bg-teal-600 text-white hover:bg-teal-700 shadow-md shadow-teal-500/20'
          }`}
      >
        <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
        <span className="truncate">
          {isFetching ? 'Buscando...' : showOnlineFallbackPrompt ? emptyActionLabel : 'Iniciar busca'}
        </span>
      </button>
    </div>
  ) : null;

  const resultsHeader = (
    <div className="px-4 sm:px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between bg-gray-50 dark:bg-gray-800/80">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 w-full">
        <div className="flex items-start gap-3 group flex-1 text-left min-w-0">
          <div className="bg-teal-500 text-white text-[10px] font-black px-2.5 py-1 rounded-full">{filteredResults.length}</div>
          <div className="flex flex-col min-w-0 flex-1">
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 min-w-0">
              <h3 className="text-sm font-black text-gray-800 dark:text-white uppercase tracking-tight">Documentos Encontrados</h3>
            </div>
            <input
              type="text"
              placeholder="Filtrar resultados..."
              value={searchTerm}
              onChange={(event) => onFilterChange(event.target.value)}
              className="bg-transparent border-none p-0 text-sm sm:text-xs text-gray-500 focus:ring-0 outline-none w-full sm:w-48 mt-0.5"
            />
          </div>
        </div>
        {showOnlineFallbackPrompt ? (
          <HeaderOnlineFallbackNotice
            isFetching={isFetching}
            onSearchOnlineFallback={onSearchOnlineFallback}
          />
        ) : null}
      </div>
    </div>
  );

  if (isMobileViewport) {
    return (
      <>
        <button
          type="button"
          aria-label="Fechar documentos encontrados"
          onClick={() => setIsMobileSheetOpen(false)}
          className={`fixed inset-0 z-20 lg:hidden ${mobileOpenProgress > 0.02 ? 'pointer-events-auto' : 'pointer-events-none'}`}
          style={{
            opacity: mobileOpenProgress > 0.02 ? 1 : 0,
            backgroundColor: 'rgba(3, 7, 18, 0.42)',
            backdropFilter: `blur(${mobileOpenProgress * 10}px)`,
            WebkitBackdropFilter: `blur(${mobileOpenProgress * 10}px)`,
            transition: draggingRef.current
              ? 'none'
              : 'backdrop-filter 280ms cubic-bezier(0.22, 1, 0.36, 1), -webkit-backdrop-filter 280ms cubic-bezier(0.22, 1, 0.36, 1)'
          }}
        />
        <div
          className="fixed inset-x-0 bottom-0 z-30 lg:hidden"
          style={{
            height: `${mobileExpandedHeight}px`,
            transform: `translateY(${currentMobileTranslate}px)`,
            transition: draggingRef.current ? 'none' : 'transform 280ms cubic-bezier(0.22, 1, 0.36, 1)'
          }}
        >
          <div
            className="h-full flex flex-col bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-2xl overflow-hidden"
            style={{
              borderTopLeftRadius: `${mobileCornerRadius}px`,
              borderTopRightRadius: `${mobileCornerRadius}px`
            }}
          >
            <div
              className="shrink-0 px-4 pt-3 pb-2 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm border-b border-gray-100 dark:border-gray-700 touch-none"
              onClick={handleSheetToggle}
              onPointerDown={beginDrag}
              onPointerMove={handleDrag}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
            >
              <div className="mx-auto h-1.5 w-14 rounded-full bg-gray-300 dark:bg-gray-600 mb-3" />
              <div className="flex items-center gap-3 min-w-0">
                <div className="bg-teal-500 text-white text-[10px] font-black px-2.5 py-1 rounded-full shrink-0">{mobileSheetBadgeValue}</div>
                <div className="min-w-0">
                  <h3 className="text-sm font-black text-gray-800 dark:text-white uppercase tracking-tight truncate">{mobileSheetTitle}</h3>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                    {mobileSheetHint}
                  </p>
                </div>
              </div>
              {showOnlineFallbackPrompt ? (
                <div className="mt-3">
                  <HeaderOnlineFallbackNotice
                    compact
                    isFetching={isFetching}
                    onSearchOnlineFallback={onSearchOnlineFallback}
                  />
                </div>
              ) : null}
            </div>

            <div className={`flex-1 min-h-0 ${isMobileSheetOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}>
              {isEmbeddedComparatorView ? (
                <div className="h-full p-4 sm:p-6">
                  {comparatorContent}
                </div>
              ) : (
                <div className="h-full flex flex-col">
                  <div
                    className="flex-1 overflow-y-auto p-4 sm:p-6 transition-all duration-300"
                    style={{
                      overscrollBehaviorY: 'contain',
                      WebkitOverflowScrolling: 'touch'
                    }}
                  >
                    <div className="pb-4 border-b border-gray-100 dark:border-gray-700 mb-4">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="flex flex-col min-w-0 flex-1">
                          <input
                            type="text"
                            placeholder="Filtrar resultados..."
                            value={searchTerm}
                            onChange={(event) => onFilterChange(event.target.value)}
                            className="bg-transparent border border-gray-200 dark:border-gray-700 px-3 h-10 rounded-xl text-sm text-gray-500 dark:text-gray-300 focus:ring-0 outline-none w-full"
                          />
                        </div>
                      </div>
                    </div>
                    <ResultsContent
                      comparableSeriesMap={comparableSeriesMap}
                      documentPortalName={documentPortalName}
                      emptyActionLabel={emptyActionLabel}
                      emptyControls={emptyControls}
                      expandedCategories={expandedCategories}
                      filteredResults={filteredResults}
                      groupedResults={groupedResults}
                      isFetching={isFetching}
                      onCompareDocument={onCompareDocument}
                      onEmptyAction={handleEmptyAction}
                      onToggleCategory={onToggleCategory}
                      searchTerm={searchTerm}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-all duration-300 relative rounded-3xl">
      {resultsHeader}

      <div className="flex-1 overflow-y-auto p-4 sm:p-6 transition-all duration-300">
        <ResultsContent
          comparableSeriesMap={comparableSeriesMap}
          documentPortalName={documentPortalName}
          emptyActionLabel={emptyActionLabel}
          emptyControls={emptyControls}
          expandedCategories={expandedCategories}
          filteredResults={filteredResults}
          groupedResults={groupedResults}
          isFetching={isFetching}
          onCompareDocument={onCompareDocument}
          onEmptyAction={handleEmptyAction}
          onToggleCategory={onToggleCategory}
          searchTerm={searchTerm}
        />
      </div>
    </div>
  );
}
